/* eslint-disable @typescript-eslint/no-explicit-any */
import IndeterminateCheckbox from '../components/base/IndeterminateCheckbox';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel
} from '@tanstack/react-table';
import { useEffect, useMemo, memo } from 'react';

const selectionColumn = {
  id: 'select',
  accessorKey: '',
  header: ({ table }) => (
    <IndeterminateCheckbox
      className="form-check fs-8 mb-0"
      {...{
        checked: table.getIsAllRowsSelected(),
        indeterminate: table.getIsSomeRowsSelected(),
        onChange: table.getToggleAllRowsSelectedHandler()
      }}
    />
  ),
  cell: ({ row }) => (
    <IndeterminateCheckbox
      className="form-check fs-8 mb-0"
      {...{
        checked: row.getIsSelected(),
        disabled: !row.getCanSelect(),
        indeterminate: row.getIsSomeSelected(),
        onChange: row.getToggleSelectedHandler()
      }}
    />
  ),
  meta: {
    headerProps: { style: { width: '30px' } }
  }
};

const useAdvanceTable = ({
  columns,
  data,
  selection,
  sortable,
  pagination,
  pageSize,
  initialState,
  // Optional. Undefined keeps tanstack's own substring match, so every existing
  // table behaves exactly as it did.
  globalFilterFn,
  // ── server-side paging ──
  // Set these three together when the rows for ONE page are all the browser
  // ever holds. The completed lists only grow — a year of production is tens of
  // thousands of rows — and shipping them all to draw twenty-five is not a page
  // size, it is a download. Left undefined, everything below is inert and the
  // table pages client-side exactly as before.
  manualPagination = false,
  pageCount,
  paginationState,
  onPaginationChange
}) => {
  const state = useMemo(
    () => ({
      pagination: { pageSize: pagination ? pageSize : data.length },
      ...initialState
    }),
    [pagination, pageSize, data.length, initialState, data]
  );

  // Call the hook directly
  const table = useReactTable({
    data,
    columns: selection ? [selectionColumn, ...columns] : columns,
    enableSorting: sortable,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // With manual paging the server has already sliced the page, so slicing it
    // again here would show one row of twenty-five.
    ...(manualPagination
      ? { manualPagination: true, pageCount: pageCount ?? -1 }
      : { getPaginationRowModel: getPaginationRowModel() }),
    ...(paginationState ? { state: { pagination: paginationState } } : {}),
    ...(onPaginationChange ? { onPaginationChange } : {}),
    ...(globalFilterFn ? { globalFilterFn } : {}),
    initialState: state,
    // Keep the planner's page across a refetch. Several screens here poll — the
    // machine queues, Project Token State, the dashboards — and with TanStack's
    // default this resets to page 1 on every poll, which makes a live table
    // impossible to read past the first ten rows.
    autoResetPageIndex: false
  });

  // …but never stand on a page that no longer exists.
  //
  // That is the other half of `autoResetPageIndex: false`, and it was missing.
  // The page index is also kept when the data SHRINKS, and a page past the end
  // renders an EMPTY table while the counts above it still say how many rows
  // there are — so it reads as "the filter is broken", not as "you are on page
  // 4 of a one-page result".
  //
  // Project Token State, 2026-10-20: 64 drums, planner on a later page, press
  // "Show Phantoms (5)" → one page of five rows exists, the table is blank. Same
  // for "Plan ahead (26)" and for every phase chip whose result is smaller than
  // the page you were on. It worked on the deployed build only because a fresh
  // visit starts on page 1.
  //
  // Clamped to the LAST page rather than the first: when a filter narrows the
  // list, the rows nearest where the planner was are the ones they were looking
  // at. `pageCount` is -1 under `manualPagination`, where the server decides the
  // slice and this must not interfere.
  // Not `pageCount` — that is already the hook's own prop, for the server-side
  // paging path above.
  const livePageCount = table.getPageCount();
  const pageIndex = table.getState()?.pagination?.pageIndex ?? 0;
  useEffect(() => {
    if (livePageCount > 0 && pageIndex > livePageCount - 1) {
      table.setPageIndex(livePageCount - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePageCount, pageIndex]);

  return table;
};

export default useAdvanceTable;
